// Custmozied antd DatePicker that takes into account timezone
// Also highlights today based on given timezone

import { DatePicker } from "antd";
import moment from "moment-timezone";
import { useMemo } from "react";

const TimezoneDatePicker = ({
  value,
  onChange,
  timezone = "UTC",
  format = "YYYY-MM-DD HH:mm:ss",
  ...props
}) => {
  // Get current date in the specified timezone
  const getCurrentDate = () => {
    return moment().tz(timezone);
  };

  // Create a custom today date that matches the timezone
  const timezonedToday = useMemo(() => {
    const today = getCurrentDate();
    return today.startOf("day").format("YYYY-MM-DD");
  }, [timezone]);

  // Helper function to convert date to target timezone
  const convertToTimezone = (date, timezone) => {
    if (!date) return null;
    return moment(date).tz(timezone);
  };

  // Helper function to convert date from target timezone to UTC
  const convertFromTimezone = (date, timezone) => {
    if (!date) return null;
    return moment.tz(date, timezone).utc();
  };

  // Handle date change and convert back to UTC before calling onChange
  const handleChange = (date) => {
    if (onChange) {
      const utcDate = date ? convertFromTimezone(date, timezone) : null;
      onChange(utcDate);
    }
  };

  // Generate DatePicker locale settings
  const customLocale = useMemo(
    () => ({
      ...props.locale,
      today: timezonedToday,
    }),
    [props.locale, timezonedToday],
  );

  // Convert the incoming value to the target timezone
  const timezonedValue = value ? convertToTimezone(value, timezone) : null;

  return (
    <DatePicker
      {...props}
      value={timezonedValue}
      onChange={handleChange}
      format={format}
      locale={customLocale}
      // Override the default date rendering to properly highlight today
      dateRender={(current, today) => {
        const currentDate = moment(current).format("YYYY-MM-DD");
        const isToday = currentDate === timezonedToday;

        return (
          <div
            className={`ant-picker-cell-inner${isToday ? " ant-picker-cell-today" : ""}`}
          >
            {current.date()}
          </div>
        );
      }}
      disabledDate={(current) => {
        if (props.disabledDate) {
          const timezonedCurrent = convertToTimezone(current, timezone);
          return props.disabledDate(timezonedCurrent);
        }
        return false;
      }}
    />
  );
};

export default TimezoneDatePicker;
